// Mints a batch of MLS KeyPackages off the freshly-bootstrapped SDK
// session and POSTs them to /v1/key-packages/replenish so peers can
// invite this device into a group.
//
// Without this, the very first time a peer tries to start a chat
// with us, GET /v1/users/{me}/key-package returns 409
// `key_package_pool_empty` and their conv.addMembers() — and
// therefore the Welcome that would let us decrypt — never happens.
//
// We always replenish on bootstrap. Skipping based on an in-Dexie
// timestamp is unsafe because the SDK's internal storage can be
// wiped (see `forgetSession` in the identity-corruption recovery
// path) independently of Dexie — leaving a "we replenished
// recently" marker even though the actual MLS state holds zero
// KeyPackages signed under the *current* device identity.
// Stacking fresh KPs per session is cheap; the BE just appends.
//
// BATCH_SIZE is also the hard ceiling on how many of an account's existing
// conversations a sibling can admit a freshly-LINKED device into in one pass:
// admission needs one DISTINCT single-use KeyPackage per chat, and the pool is
// primed once here at bootstrap. It is set to 20 to MATCH iOS
// (`MessagingService.uploadFreshKeyPackages`) so a linked web device with up to
// 20 conversations is fully admitted on the first pass. Accounts with MORE than
// 20 conversations still need the reconciler to trigger a remote replenish + a
// last-resort-KP fallback and retry — tracked as the remaining H1 delivery work
// (see docs/architecture/multi-device.md linking-admission notes).

import { log } from '@core/log/log';
import { get, NetworkError, post } from '@core/network/api';
import { db } from '@core/persistence/db';

// Kept as a "we ran replenish at least once" marker so observability/
// tests can detect when the bootstrap path has primed the pool. The
// value is informational — we no longer gate replenish on it.
const REPLENISH_KEY = '__session.keyPackagesReplenishedAt';
const BATCH_SIZE = 20;

// Bootstrap-time replenish is the ONLY thing that primes a fresh device's
// KeyPackage pool — peers can't invite us until it lands (their
// `addMembers` consumes one of our published KPs; an empty pool 410s with
// `key_package_pool_empty`). It used to be one-shot and best-effort, so a
// single transient failure left the pool empty FOREVER (until the next
// reload) and the user silently became un-invitable. The two transient
// failure windows that bite a brand-new signup:
//   1. The device-activation race — right after verify the device row may
//      not be active yet, so the first replenish 401/403s (see the
//      `no conversations` race note in sessionBootstrap).
//   2. The SDK keystore not being ready, so `freshKeyPackage()` yields an
//      empty batch on the first tick.
// Both are temporary, so we retry with exponential backoff. We do NOT
// retry a terminal `device_revoked` (publishing on a revoked device can
// never succeed — the user must re-auth).
const MAX_ATTEMPTS = 5;
const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 16_000;

function retryDelayMs(attempt: number): number {
  const backoff = Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS);
  return backoff + Math.floor(Math.random() * 250);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** A `device_revoked` 403 is the one failure retrying can't fix. */
function isTerminalReplenishError(err: unknown): boolean {
  return (
    err instanceof NetworkError &&
    err.status === 403 &&
    err.serverError === 'device_revoked'
  );
}

interface FreshKeyPackageProducer {
  freshKeyPackage(): Promise<Uint8Array>;
  // Optional: present on ping-openmls-sdk >= 0.7.10. A device publishes ONE
  // last-resort KeyPackage that the server serves repeatedly (un-consumed) as
  // the fallback when the single-use pool empties — so peers can always invite
  // this device. Optional-typed so older SDKs / test doubles still satisfy it.
  freshLastResortKeyPackage?(): Promise<Uint8Array>;
}

interface RejectedKeyPackageWire {
  readonly index: number;
  readonly reason: string;
}

interface ReplenishResponse {
  readonly accepted: number;
  readonly rejected: readonly RejectedKeyPackageWire[];
}

/**
 * Mint a batch of fresh KeyPackages off the SDK. Returns whatever it
 * could produce — an empty array means the SDK keystore isn't ready yet
 * (a transient bootstrap-race condition the caller retries).
 */
async function mintBatch(client: FreshKeyPackageProducer): Promise<Uint8Array[]> {
  const packages: Uint8Array[] = [];
  for (let i = 0; i < BATCH_SIZE; i++) {
    // `freshKeyPackage()` is typed `Promise<Uint8Array>`, but in
    // practice it can either resolve to `null` OR throw — the
    // identity-corruption recovery path wipes the SDK keystore and
    // a subsequent call before the SDK fully re-keys ends up
    // calling `.length` on null inside the wasm boundary. We
    // catch BOTH failure modes per-iteration so one bad call
    // doesn't take down the whole batch and starve our pool.
    let kp: Uint8Array | null = null;
    try {
      kp = await client.freshKeyPackage();
    } catch (innerErr) {
      log.warn({
        event: 'keyPackages.replenish.iterationFailed',
        attempt: i,
        error: (innerErr as Error).message,
      });
      continue;
    }
    if (kp == null || !(kp instanceof Uint8Array) || kp.length === 0) {
      log.warn({ event: 'keyPackages.replenish.skippedNull', attempt: i });
      continue;
    }
    packages.push(kp);
  }
  return packages;
}

// Bootstrap skips the upload when the device's server-visible pool is already
// at or above this — stops the pool creeping up on every reload (each bootstrap
// used to add a full batch unconditionally, so N reloads left N×BATCH_SIZE
// unconsumed KeyPackages until their 30-day expiry). Half a batch keeps a
// comfortable margin above the foreground LOW_WATERMARK.
const BOOTSTRAP_HEALTHY_POOL = 10;

/**
 * Prime this device's KeyPackage pool.
 *
 * `forceFull` (default false) — when true, ALWAYS upload a fresh batch,
 * bypassing the server-count check. Pass it whenever the on-server KeyPackages
 * are unusable regardless of their count: a fresh identity, or a bootstrap right
 * after the SDK MLS store was wiped (device-id realign / storage corruption),
 * where the private init keys behind the published KeyPackages are gone.
 *
 * When `forceFull` is false, the upload is SKIPPED if the server already has a
 * healthy pool for this device (`GET /v1/key-packages/count` ≥
 * {@link BOOTSTRAP_HEALTHY_POOL}). The count is keyed by device_id, so a fresh
 * device (count 0) or a drained pool still replenishes.
 */
export async function replenishKeyPackages(
  client: FreshKeyPackageProducer,
  opts?: { readonly forceFull?: boolean },
): Promise<void> {
  if (opts?.forceFull !== true) {
    try {
      const resp = await get<{ count?: number }>('/v1/key-packages/count', {
        headers: { 'X-Ping-Skip-Reauth': '1' },
      });
      const count = typeof resp.count === 'number' ? resp.count : 0;
      if (count >= BOOTSTRAP_HEALTHY_POOL) {
        log.info({ event: 'keyPackages.replenish.skipHealthy', count });
        return;
      }
    } catch {
      // Count unknown (network blip / not-yet-active device) → fall through and
      // replenish. Uploading when unsure is the safe default (an empty pool is
      // worse than a slightly-larger one).
    }
  }
  // Retry with exponential backoff: priming the pool is the difference
  // between being invitable and silently not, so a transient bootstrap
  // failure (device-activation race, SDK not ready, network blip) must not
  // leave the pool permanently empty — see the header note.
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const isLastAttempt = attempt === MAX_ATTEMPTS - 1;
    try {
      const packages = await mintBatch(client);
      if (packages.length === 0) {
        // SDK keystore not ready yet — retry unless we're out of attempts.
        if (!isLastAttempt) {
          await delay(retryDelayMs(attempt));
          continue;
        }
        log.warn({ event: 'keyPackages.replenish.empty_batch' });
        return;
      }

      // Canonical body shape (openapi.yaml `KeyPackageReplenishRequest`):
      //
      //   { key_packages: string[] }   // each entry is base64url of
      //                                // the raw MLS KeyPackage bytes
      //
      // Earlier iterations wrapped each entry in
      // `{ ciphersuite, package_bytes }` and included `replace_existing`
      // — both were rejected by the server's `DisallowUnknownFields`
      // decoder (services/api/internal/keypackage/service.go) and the
      // shape mismatch on `key_packages` itself triggered a flat 400
      // `invalid_request`. The server also expects base64url (`-_`,
      // no padding), not standard base64 (`+/=`) — std-b64 entries
      // would decode as `invalid_encoding` even after the shape fix.
      // Mint ONE last-resort KeyPackage (RFC 9420 §10) alongside the single-use
      // batch so peers can always invite this device even after the pool drains
      // (the server serves it un-consumed as the fallback — see G5). Best-effort
      // and version-guarded: a transient failure or an older SDK without the
      // method must not block the single-use replenish that primes invitability.
      let lastResort: string[] | undefined;
      if (typeof client.freshLastResortKeyPackage === 'function') {
        try {
          const lr = await client.freshLastResortKeyPackage();
          if (lr instanceof Uint8Array && lr.length > 0) {
            lastResort = [uint8ArrayToBase64Url(lr)];
          }
        } catch (lrErr) {
          log.warn({
            event: 'keyPackages.replenish.lastResortFailed',
            error: (lrErr as Error).message,
          });
        }
      }

      const body = {
        key_packages: packages.map(uint8ArrayToBase64Url),
        ...(lastResort !== undefined ? { last_resort_key_packages: lastResort } : {}),
      };

      // Background maintenance: opt out of the global 401→logout interceptor.
      // A transient/raced 401 here must never tear down the session (the next
      // foreground call catches a genuine expiry). See interceptor.ts.
      const resp = await post<ReplenishResponse>('/v1/key-packages/replenish', body, {
        headers: { 'X-Ping-Skip-Reauth': '1' },
      });

      await db.settings_kv.put({
        key: REPLENISH_KEY,
        value: Date.now(),
        updatedAt: Date.now(),
      });

      log.info({
        event: 'keyPackages.replenish.ok',
        attempt,
        accepted: resp.accepted,
        // Go marshals nil slices as JSON `null`, not `[]`, so the BE
        // response shape is `{ accepted, rejected: null }` when the
        // whole batch was accepted. Reading `.length` on null crashed
        // here with the canonical "Cannot read properties of null
        // (reading 'length')" — the upload had ACTUALLY SUCCEEDED on
        // the server, we just blew up on the log line.
        rejected: resp.rejected?.length ?? 0,
      });
      return;
    } catch (err) {
      // A revoked device can never publish — retrying is pointless. The
      // user must re-authenticate (the auth layer now signals this).
      if (isTerminalReplenishError(err)) {
        log.warn({ event: 'keyPackages.replenish.deviceRevoked' });
        return;
      }
      if (!isLastAttempt) {
        log.warn({
          event: 'keyPackages.replenish.retrying',
          attempt,
          error: (err as Error).message,
        });
        await delay(retryDelayMs(attempt));
        continue;
      }
      // Out of attempts. Non-fatal: the user can still SEND (sender-side
      // flow works without our own KeyPackages), but peers can't invite us
      // until a later bootstrap succeeds.
      log.error({
        event: 'keyPackages.replenish.failed',
        attempt,
        error: (err as Error).message,
      });
      return;
    }
  }
}

// Low-watermark threshold. Peers consume ONE of this device's single-use
// KeyPackages every time they invite/admit it, so a device that's contacted a
// lot drains its bootstrap pool over time and eventually becomes un-invitable
// (peers then fall back to the reusable last-resort KP, but a healthy single-use
// pool is strongly preferred — see the admission-claim flow). Matches the
// umbrella "replenish when server-visible count < 5" trigger.
const LOW_WATERMARK = 5;

let topUpInFlight = false;

/**
 * Opportunistic low-watermark top-up: if the device's server-visible single-use
 * KeyPackage pool has dropped below {@link LOW_WATERMARK}, upload a fresh batch.
 * Call this on cheap resume signals (foreground / WS reconnect / online) — it's
 * a single GET when the pool is healthy, and only replenishes when it isn't.
 * De-duped so overlapping resume events don't stack uploads.
 */
export async function maybeTopUpKeyPackages(client: FreshKeyPackageProducer): Promise<void> {
  if (topUpInFlight) return;
  topUpInFlight = true;
  try {
    let count: number | null = null;
    try {
      // Background maintenance: never let a transient/raced 401 here tear down
      // the session (mirror of the replenish call's opt-out).
      const resp = await get<{ count?: number }>('/v1/key-packages/count', {
        headers: { 'X-Ping-Skip-Reauth': '1' },
      });
      count = typeof resp.count === 'number' ? resp.count : null;
    } catch {
      return; // unknown count — skip this tick
    }
    if (count === null || count >= LOW_WATERMARK) return;
    log.info({ event: 'keyPackages.topUp.belowWatermark', count, watermark: LOW_WATERMARK });
    await replenishKeyPackages(client);
  } finally {
    topUpInFlight = false;
  }
}

function uint8ArrayToBase64Url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] ?? 0);
  // Convert standard base64 → base64url (no padding).
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
